'use client';

import type { PostHeroProps } from '@routeroyal/theme-core';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useState } from 'react';

/**
 * AllysPostHero - Allys-themed post listing hero
 * Premium hero section with Allys design language
 */
export function AllysPostHero({
  title,
  subtitle,
  backgroundImage,
  showSearch = false,
  onSearch,
  searchPlaceholder = 'Search posts...',
  className = '',
}: PostHeroProps) {
  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch?.(value);
  };

  return (
    <section
      className={`
        relative py-16 md:py-28 lg:py-36 overflow-hidden
        [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
        ${className}
      `}
    >
      {/* Background Image */}
      {backgroundImage && (
        <div className="absolute inset-0 z-0">
          <Image
            src={backgroundImage}
            alt=""
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#243462]/80 to-[#243462]/60" />
          
          {/* Grain texture overlay */}
          <div 
            className="absolute inset-0 opacity-[0.15] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '180px 180px'
            }}
          />
        </div>
      )}

      {/* Fallback background */}
      {!backgroundImage && (
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#243462] to-[#121C3B]">
          <div 
            className="absolute inset-0 opacity-[0.15] mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '180px 180px'
            }}
          />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-3xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 tracking-tight leading-tight text-white"
            style={{
              textShadow: '0 4px 60px rgba(0,0,0,0.5), 0 2px 30px rgba(0,0,0,0.4)'
            }}
          >
            {title}
          </motion.h1>
          
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-base md:text-2xl mb-6 md:mb-8 leading-relaxed font-light text-white/90"
              style={{
                textShadow: '0 2px 30px rgba(0,0,0,0.5)'
              }}
            >
              {subtitle}
            </motion.p>
          )}

          {/* Search Bar */}
          {showSearch && onSearch && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="max-w-2xl mx-auto mt-4 md:mt-8"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] rounded-full opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />
                <div className="relative">
                  <input
                    type="text"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder={searchPlaceholder}
                    className="
                      w-full px-6 py-4 pr-14 rounded-full
                      text-[#243462] placeholder-gray-400
                      bg-white/95 backdrop-blur-sm
                      border-2 border-transparent
                      focus:ring-0 focus:border-[#BBA339]
                      shadow-2xl
                      transition-all duration-300
                      text-lg
                    "
                  />
                  <button
                    type="button"
                    className="
                      absolute right-2 top-1/2 -translate-y-1/2
                      p-3 rounded-full
                      bg-[#BBA339] text-white
                      hover:bg-[#243462]
                      transition-colors duration-300
                    "
                    aria-label="Search"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Decorative gold line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#BBA339] to-transparent" />
    </section>
  );
}
